function toAccountList(accounts) {
  return Array.isArray(accounts) ? accounts : [];
}

function findTopmostAccessibleAccount(accountsById, account) {
  const visited = new Set();
  let current = account;
  while (current && !visited.has(Number(current.id))) {
    visited.add(Number(current.id));
    const parent = accountsById.get(Number(current.father_account));
    if (!parent) return current;
    current = parent;
  }
  return current;
}

function getContactLayerKey(accountsById, account) {
  const topmostAccount = findTopmostAccessibleAccount(accountsById, account);
  return Number(
    account?.root_account_id ??
      topmostAccount?.root_account_id ??
      topmostAccount?.father_account ??
      topmostAccount?.id,
  );
}

function indexAccountsById(accountList) {
  return new Map(accountList.map((account) => [Number(account?.id), account]));
}

/**
 * Devuelve una opción por capa de contactos (cuenta raíz) accesible.
 *
 * Los créditos y la base de contactos viven en la raíz de cada jerarquía, así
 * que dos cuentas hermanas cobran en el mismo lugar y no ameritan un selector.
 * Cada opción conserva el id de una cuenta accesible de la jerarquía (la de
 * más arriba, priorizando la raíz) y se nombra como la raíz.
 */
export function getSelectableAccounts(accounts) {
  const accountList = toAccountList(accounts);
  const accountsById = indexAccountsById(accountList);
  const optionByLayerKey = new Map();
  for (const account of accountList) {
    if (accountsById.has(Number(account?.father_account))) continue;
    const layerKey = getContactLayerKey(accountsById, account);
    const existingOption = optionByLayerKey.get(layerKey);
    const isRootAccount = account.father_account == null;
    if (existingOption && !isRootAccount) continue;
    optionByLayerKey.set(layerKey, {
      ...account,
      name: account.root_account_name ?? account.name,
    });
  }
  return [...optionByLayerKey.values()];
}

/**
 * Devuelve el id de la opción de getSelectableAccounts que corresponde a la
 * capa de contactos de `accountId`, o null si la cuenta no está en la lista.
 */
export function resolveSelectableAccountId(accounts, accountId) {
  if (accountId == null) return null;
  const accountList = toAccountList(accounts);
  const accountsById = indexAccountsById(accountList);
  const account = accountsById.get(Number(accountId));
  if (!account) return null;
  const layerKey = getContactLayerKey(accountsById, account);
  const matchingOption = getSelectableAccounts(accountList).find(
    (option) => getContactLayerKey(accountsById, option) === layerKey,
  );
  return matchingOption ? Number(matchingOption.id) : null;
}

export function getAccountName(accounts, accountId) {
  if (accountId == null) return null;
  const match = toAccountList(accounts).find(
    (account) => Number(account?.id) === Number(accountId),
  );
  return match?.name ?? null;
}

/**
 * Devuelve una copia de las cuentas ordenada alfabéticamente por nombre,
 * sin distinguir mayúsculas ni tildes.
 */
export function sortAccountsByName(accounts) {
  return [...toAccountList(accounts)].sort((firstAccount, secondAccount) =>
    (firstAccount?.name ?? "").localeCompare(secondAccount?.name ?? "", "es", {
      sensitivity: "base",
    }),
  );
}
